import { useState, useEffect, useRef } from 'react';
import { Rocket, Target, Trophy, Settings } from 'lucide-react';

const SLIDE_MS = 4500;

const THEME_PRESETS = {
  orange: { 
    accent: '#BF5700',      // Requested Orange
    accentDark: '#8A3F00', 
    accentLight: '#BF570022',
    Icon: Target 
  },
  blue: { 
    accent: '#0d1f2d',      // Requested Dark Blue
    accentDark: '#000000', 
    accentLight: '#0d1f2d22',
    Icon: Rocket 
  },
  green: { 
    accent: '#0b5d1e',      // Requested Green
    accentDark: '#052e0f', 
    accentLight: '#0b5d1e22',
    Icon: Trophy 
  },
};

const getGoalTheme = (goal, idx, total) => {
  let presetKey = 'orange'; // default fallback
  
  if (total === 1) {
    presetKey = 'orange';
  } else if (total === 2) {
    presetKey = idx === 0 ? 'orange' : 'blue';
  } else if (total === 3) {
    if (goal.period === 'monthly') presetKey = 'blue';
    else if (goal.period === 'quarterly') presetKey = 'orange';
    else if (goal.period === 'yearly') presetKey = 'green';
  }
  
  const preset = THEME_PRESETS[presetKey];
  
  let label = 'This Month';
  if (goal.period === 'quarterly') label = 'This Quarter';
  else if (goal.period === 'yearly') label = 'This Year';
  
  return {
    ...preset,
    label
  };
};

export default function CompanyGoalCarousel({ goals, isAdmin, onConfigure }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const resumeTimer = useRef(null);

  useEffect(() => {
    if (index >= goals.length) setIndex(0);
  }, [goals.length, index]);

  useEffect(() => {
    if (paused || goals.length < 2) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % goals.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, paused, goals.length]);

  const goTo = (i) => {
    setIndex((i + goals.length) % goals.length);
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), 6000);
  };

  const goal = goals[index] || goals[0];
  if (!goal) return null;
  const theme = getGoalTheme(goal, index, goals.length);
  const Icon = theme.Icon;

  return (
    <div className="px-4">
      <div
        className={`relative rounded-xl p-3 pt-[10px] overflow-hidden bg-white ${goals.length > 1 ? 'cursor-pointer select-none' : (isAdmin ? 'cursor-pointer' : '')}`}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (dx > 40) goTo(index - 1);
          else if (dx < -40) goTo(index + 1);
          touchX.current = null;
        }}
        onClick={(e) => {
          if (e.target.closest('button')) return;

          if (goals.length < 2) {
            if (isAdmin && onConfigure) onConfigure();
            return;
          }

          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const halfWidth = rect.width / 2;

          if (clickX < halfWidth) {
            goTo(index - 1);
          } else {
            goTo(index + 1);
          }
        }}
      >
        <style>{`
          @keyframes fillbar { from { width: 0%; } to { width: 100%; } }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(12px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-slide-in {
            animation: slideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}</style>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-text-color text-[18px] font-semibold text-left">
              {goal.period === 'monthly' ? 'Goal for the Month' : goal.period === 'quarterly' ? 'Goal for the Quarter' : 'Goal for the Year'}
            </span>
          </div>
          {isAdmin && onConfigure && (
            <button
              type="button"
              aria-label="Configure goals"
              onClick={(e) => { e.stopPropagation(); onConfigure(); }}
              className="p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius shrink-0"
            >
              <Settings className="h-5 w-5" />
            </button>
          )}
        </div>

        <div key={index} className="animate-slide-in my-1">
          <p className="text-[15px] text-text-color opacity-80 leading-normal font-semibold text-left">
            {goal.text}
          </p>
        </div>

        {goals.length > 1 && ( <div className="justify-center flex items-center mt-3">
            <div className="flex gap-1 w-[30%]">
              {goals.map((g, i) => (
                <button key={i} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`}
                  className="flex-1 rounded-full overflow-hidden" style={{ height: 3, background: '#f0f0f0' }}>
                  <div
                    key={i === index ? `${index}-${paused}` : `s${i}`}
                    style={{
                      height: '100%',
                      background: theme.accent,
                      width: i < index ? '100%' : i > index ? '0%' : paused ? '50%' : undefined,
                      animation: i === index && !paused ? `fillbar ${SLIDE_MS}ms linear forwards` : 'none',
                    }}
                  />
                </button>
              ))}
            </div>
        </div>          )}
      </div>
    </div>
  );
}